import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import posService from "services/posService";

const initialState = {
  loading: false,
  terminals: [],
  sessions: [],
  orders: [],
  ordersCount: 0,
  ordersNext: null,
  ordersPrevious: null,
  ordersSummary: null,
  selectedOrder: null,
  salesReport: null,
  productReport: [],
  cashierPerformance: [],
  message: "",
  showMessage: false,
};

// ========== Terminal Thunks ==========

export const getTerminals = createAsyncThunk(
  "pos/getTerminals",
  async (_, { rejectWithValue }) => {
    try {
      const response = await posService.getTerminals();
      return response.data || response;
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching terminals");
    }
  }
);

export const createTerminal = createAsyncThunk(
  "pos/createTerminal",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await posService.createTerminal(data);
      dispatch(getTerminals());
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error creating terminal");
    }
  }
);

export const updateTerminal = createAsyncThunk(
  "pos/updateTerminal",
  async ({ id, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await posService.updateTerminal(id, data);
      dispatch(getTerminals());
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error updating terminal");
    }
  }
);

export const deleteTerminal = createAsyncThunk(
  "pos/deleteTerminal",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await posService.deleteTerminal(id);
      dispatch(getTerminals());
      return { id, message: "Terminal deleted successfully" };
    } catch (err) {
      return rejectWithValue(err.message || "Error deleting terminal");
    }
  }
);

// ========== Session Thunks ==========

export const getSessions = createAsyncThunk(
  "pos/getSessions",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await posService.getSessions(params);
      return response.data || response;
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching sessions");
    }
  }
);

export const openSession = createAsyncThunk(
  "pos/openSession",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await posService.openSession(data);
      dispatch(getSessions());
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error opening session");
    }
  }
);

export const closeSession = createAsyncThunk(
  "pos/closeSession",
  async ({ sessionId, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await posService.closeSession(sessionId, data);
      dispatch(getSessions());
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error closing session");
    }
  }
);

// ========== Order Thunks ==========

export const getOrders = createAsyncThunk(
  "pos/getOrders",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await posService.getOrders(params);
      // API returns: { count, next, previous, results: { success, data: [...], filters } }
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching orders");
    }
  }
);

export const getOrderById = createAsyncThunk(
  "pos/getOrderById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await posService.getOrderById(id);
      return response.data || response;
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching order");
    }
  }
);

export const voidOrder = createAsyncThunk(
  "pos/voidOrder",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await posService.updateOrder(id, { action: "void" });
      dispatch(getOrders());
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error voiding order");
    }
  }
);

export const refundOrder = createAsyncThunk(
  "pos/refundOrder",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await posService.updateOrder(id, { action: "refund" });
      dispatch(getOrders());
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error refunding order");
    }
  }
);

// ========== Report Thunks ==========

export const getSalesReport = createAsyncThunk(
  "pos/getSalesReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await posService.getSalesReport(params);
      return response.data || response;
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching sales report");
    }
  }
);

export const getProductReport = createAsyncThunk(
  "pos/getProductReport",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await posService.getProductReport(params);
      return response.data || response;
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching product report");
    }
  }
);

export const getCashierPerformance = createAsyncThunk(
  "pos/getCashierPerformance",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await posService.getCashierPerformance(params);
      return response.data || response;
    } catch (err) {
      return rejectWithValue(
        err.message || "Error fetching cashier performance"
      );
    }
  }
);

// ========== Slice ==========

const posSlice = createSlice({
  name: "pos",
  initialState,
  reducers: {
    clearPosMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
    clearSelectedOrder: (state) => {
      state.selectedOrder = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Terminals
      .addCase(getTerminals.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTerminals.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.terminals = payload;
      })
      .addCase(getTerminals.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(createTerminal.pending, (state) => {
        state.loading = true;
      })
      .addCase(createTerminal.fulfilled, (state) => {
        state.loading = false;
        state.message = "Terminal created successfully";
        state.showMessage = true;
      })
      .addCase(createTerminal.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(deleteTerminal.fulfilled, (state, { payload }) => {
        state.terminals = state.terminals.filter(
          (t) => t.terminal_id !== payload.id
        );
        state.message = payload.message;
        state.showMessage = true;
      })
      .addCase(deleteTerminal.rejected, (state, { payload }) => {
        state.message = payload;
        state.showMessage = true;
      })

      // Sessions
      .addCase(getSessions.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSessions.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.sessions = payload;
      })
      .addCase(getSessions.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(openSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(openSession.fulfilled, (state) => {
        state.loading = false;
        state.message = "Session opened successfully";
        state.showMessage = true;
      })
      .addCase(openSession.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(closeSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(closeSession.fulfilled, (state) => {
        state.loading = false;
        state.message = "Session closed successfully";
        state.showMessage = true;
      })
      .addCase(closeSession.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })

      // Orders
      .addCase(getOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOrders.fulfilled, (state, { payload }) => {
        state.loading = false;
        const results = payload?.results;
        if (Array.isArray(results)) {
          state.orders = results;
        } else if (results?.data && Array.isArray(results.data)) {
          state.orders = results.data;
        } else {
          state.orders = Array.isArray(payload) ? payload : [];
        }
        state.ordersCount = payload?.count || 0;
        state.ordersNext = payload?.next || null;
        state.ordersPrevious = payload?.previous || null;
        state.ordersSummary = payload?.summary || null;
      })
      .addCase(getOrders.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(getOrderById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOrderById.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.selectedOrder = payload;
      })
      .addCase(getOrderById.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(voidOrder.fulfilled, (state) => {
        state.message = "Order voided successfully";
        state.showMessage = true;
      })
      .addCase(voidOrder.rejected, (state, { payload }) => {
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(refundOrder.fulfilled, (state) => {
        state.message = "Order refunded successfully";
        state.showMessage = true;
      })
      .addCase(refundOrder.rejected, (state, { payload }) => {
        state.message = payload;
        state.showMessage = true;
      })

      // Reports
      .addCase(getSalesReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSalesReport.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.salesReport = payload;
      })
      .addCase(getSalesReport.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(getProductReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProductReport.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.productReport = payload;
      })
      .addCase(getProductReport.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(getCashierPerformance.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCashierPerformance.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.cashierPerformance = payload;
      })
      .addCase(getCashierPerformance.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      });
  },
});

export const { clearPosMessage, clearSelectedOrder } = posSlice.actions;
export default posSlice.reducer;
